/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { IUserFile } from '@leedpulse-monorepo/interfaces';
import { Authentication } from '@leedpulse-monorepo/classes';
import getUserFiles from '../../../../domain/use_cases/users/getUserFiles';

export const USER_FILES_FETCH_LIMIT = 20;

const UserFilesContext = createContext({
  count: 0,
  hitsPerPage: USER_FILES_FETCH_LIMIT,
  items: [] as IUserFile[],
  scopedUserUid: null as string | null,
  fetch: (_: string | null, __: Authentication | null, ___?: number, ____?: number) => {},
  reset: () => {},
  status: ContextStatus.IDLE,
});

export const UserFilesProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(USER_FILES_FETCH_LIMIT);
  const [items, setItems] = useState([] as IUserFile[]);
  const [scopedUserUid, setScopedUserUid] = useState(null as string | null);
  const [status, setStatus] = useState(ContextStatus.IDLE);

  const fetch = async (
    userUid: string | null,
    auth: Authentication | null,
    page = 1,
    pageSize?: number
  ) => {
    const effectiveLimit = pageSize ?? hitsPerPage;
    if (pageSize !== undefined) setHitsPerPage(pageSize);
    setScopedUserUid(userUid);
    setStatus(ContextStatus.LOADING);
    try {
      if (!userUid) {
        setItems([]);
        setCount(0);
        setStatus(ContextStatus.SUCCESS);
        return;
      }
      const useCaseResponse = await getUserFiles(userUid, effectiveLimit, page, auth);
      setItems(useCaseResponse.items);
      setCount(useCaseResponse.totalCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setCount(0);
    setHitsPerPage(USER_FILES_FETCH_LIMIT);
    setItems([]);
    setScopedUserUid(null);
    setStatus(ContextStatus.IDLE);
  };

  return (
    <UserFilesContext.Provider
      value={{
        count,
        hitsPerPage,
        items,
        scopedUserUid,
        fetch,
        reset,
        status,
      }}
    >
      {children}
    </UserFilesContext.Provider>
  );
};

export default UserFilesContext;
